import { apiFetch } from "../lib/api";

export type BrokerKind = "zerodha" | "dhan" | "delta" | "upstox";

/** Brokers you can connect for historical data but cannot route orders through:
 *  there is no order adapter, only a data source. Mirrors DATA_ONLY_BROKERS in
 *  webapp/services/broker_factory.py — a connection here is still useful for
 *  backfills, it just must never appear in a deployment's broker_map. */
export const DATA_ONLY_BROKERS: BrokerKind[] = ["upstox"];

export interface BrokerConnection {
  id: string;
  broker: BrokerKind;
  label: string;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  credential_keys: string[];
  /** Non-secret broker tuning. Unlike credentials this comes back in full. */
  settings: Record<string, number>;
}

export interface BrokerConnectionCreate {
  broker: BrokerKind;
  label?: string;
  credentials: Record<string, string>;
  notes?: string | null;
  settings?: Record<string, number>;
}

/** One editable setting, described by the server so the form doesn't hardcode
 *  bounds or help text that could drift from the validator. */
export interface BrokerSettingSpec {
  key: string;
  label: string;
  minimum: number;
  maximum: number;
  default: number;
  help: string;
}

export async function getBrokerSettingsSchema(): Promise<
  Record<string, BrokerSettingSpec[]>
> {
  return apiFetch<Record<string, BrokerSettingSpec[]>>(
    "/broker-connections/settings-schema",
  );
}

export async function listBrokerConnections(): Promise<BrokerConnection[]> {
  return apiFetch<BrokerConnection[]>("/broker-connections");
}

export async function createBrokerConnection(
  payload: BrokerConnectionCreate,
): Promise<BrokerConnection> {
  return apiFetch<BrokerConnection>("/broker-connections", {
    method: "POST",
    body: payload,
  });
}

export async function deleteBrokerConnection(id: string): Promise<void> {
  await apiFetch<void>(`/broker-connections/${id}`, { method: "DELETE" });
}

export interface BrokerConnectionUpdate {
  credentials?: Record<string, string>;
  label?: string;
  notes?: string | null;
  is_active?: boolean;
  /** Replaces the stored bag wholesale — send every field the form renders. */
  settings?: Record<string, number>;
}

export async function updateBrokerConnection(
  id: string,
  payload: BrokerConnectionUpdate,
): Promise<BrokerConnection> {
  return apiFetch<BrokerConnection>(`/broker-connections/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export const REQUIRED_CREDENTIAL_FIELDS: Record<BrokerKind, string[]> = {
  zerodha: ["api_key", "api_secret", "access_token"],
  dhan: ["client_id", "access_token"],
  delta: ["api_key", "api_secret"],
  upstox: ["api_key", "api_secret", "redirect_uri", "access_token"],
};

export interface ZerodhaExchangeResponse {
  access_token: string;
  expires_at_note: string;
}

export async function exchangeZerodhaToken(payload: {
  api_key: string;
  api_secret: string;
  request_token: string;
}): Promise<ZerodhaExchangeResponse> {
  return apiFetch<ZerodhaExchangeResponse>("/broker-connections/zerodha/exchange-token", {
    method: "POST",
    body: payload,
  });
}

export async function refreshZerodhaTokenForConnection(
  connectionId: string,
  request_token: string,
): Promise<BrokerConnection> {
  return apiFetch<BrokerConnection>(
    `/broker-connections/${connectionId}/refresh-zerodha-token`,
    {
      method: "POST",
      body: { request_token },
    },
  );
}

export function zerodhaLoginUrl(apiKey: string): string {
  return `https://kite.zerodha.com/connect/login?api_key=${encodeURIComponent(apiKey)}&v=3`;
}

export interface UpstoxExchangeResponse {
  access_token: string;
  expires_at_note: string;
}

export async function exchangeUpstoxToken(payload: {
  api_key: string;
  api_secret: string;
  redirect_uri: string;
  code: string;
}): Promise<UpstoxExchangeResponse> {
  return apiFetch<UpstoxExchangeResponse>("/broker-connections/upstox/exchange-token", {
    method: "POST",
    body: payload,
  });
}

export async function refreshUpstoxTokenForConnection(
  connectionId: string,
  code: string,
): Promise<BrokerConnection> {
  return apiFetch<BrokerConnection>(
    `/broker-connections/${connectionId}/refresh-upstox-token`,
    {
      method: "POST",
      body: { code },
    },
  );
}

export function upstoxLoginUrl(apiKey: string, redirectUri: string): string {
  return (
    "https://api.upstox.com/v2/login/authorization/dialog" +
    `?response_type=code&client_id=${encodeURIComponent(apiKey)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}`
  );
}

// ---------------------------------------------------------------------------
// Health check
// ---------------------------------------------------------------------------

export type BrokerHealthStatus =
  | "ok"
  | "expired"
  | "invalid"
  | "missing_sdk"
  | "network_error"
  | "error";

export interface BrokerHealthRemedy {
  action:
    | "refresh_zerodha_token"
    | "regenerate_dhan_token"
    | "regenerate_delta_keys"
    | string;
  login_url?: string | null;
  url?: string | null;
  instructions?: string | null;
}

export interface BrokerMarginLine {
  label: string;
  amount: string; // 2dp decimal string
  currency: string; // "INR", or a Delta asset ("USD", ...)
}

export interface BrokerHealthResult {
  status: BrokerHealthStatus;
  message: string;
  details?: Record<string, unknown> | null;
  remedy?: BrokerHealthRemedy | null;
  /** Available margin, fetched by the test (and by the re-test after a token
   *  refresh). `margin_error` says why it is missing when the login worked. */
  margin?: BrokerMarginLine[] | null;
  margin_error?: string | null;
}

export async function testBrokerConnection(id: string): Promise<BrokerHealthResult> {
  return apiFetch<BrokerHealthResult>(`/broker-connections/${id}/test`, {
    method: "POST",
  });
}

export interface InstrumentsSnapshotResult {
  rows: number;
  snapshot_date: string; // "YYYY-MM-DD"
}

export async function snapshotInstruments(
  id: string,
): Promise<InstrumentsSnapshotResult> {
  return apiFetch<InstrumentsSnapshotResult>(
    `/broker-connections/${id}/snapshot-instruments`,
    { method: "POST" },
  );
}
